import { useState } from "react";
import { Button } from "./Button";
import { ErrorBanner } from "./ErrorBanner";
import { strings } from "../lib/strings";
import { PASSWORD_MIN_LENGTH } from "../lib/env";

interface PasswordFormProps {
  isSubmitting: boolean;
  onSubmit: (password: string) => Promise<void> | void;
  serverError: string | null;
}

export function PasswordForm({
  isSubmitting,
  onSubmit,
  serverError,
}: PasswordFormProps): JSX.Element {
  const [password, setPassword] = useState<string>("");
  const [confirm, setConfirm] = useState<string>("");
  const [show, setShow] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (): void => {
    if (password.length < PASSWORD_MIN_LENGTH) {
      setValidationError(strings.password.tooShort);
      return;
    }
    if (password !== confirm) {
      setValidationError(strings.password.mismatch);
      return;
    }
    setValidationError(null);
    void onSubmit(password);
  };

  const inputType: string = show ? "text" : "password";
  const toggleLabel: string = show ? strings.password.hide : strings.password.show;

  return (
    <>
      <div className="field">
        <label htmlFor="new-password" className="field__label">
          {strings.password.newPasswordLabel}
        </label>
        <div className="password-toggle">
          <input
            id="new-password"
            type={inputType}
            className="field__input"
            autoComplete="new-password"
            placeholder={strings.password.placeholder}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setValidationError(null);
            }}
            disabled={isSubmitting}
          />
          <button
            type="button"
            className="password-toggle__btn"
            onClick={() => setShow((prev) => !prev)}
          >
            {toggleLabel}
          </button>
        </div>
      </div>
      <div className="field">
        <label htmlFor="confirm-password" className="field__label">
          {strings.password.confirmPasswordLabel}
        </label>
        <input
          id="confirm-password"
          type={inputType}
          className="field__input"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            setValidationError(null);
          }}
          disabled={isSubmitting}
        />
      </div>
      <ErrorBanner message={validationError ?? serverError} />
      <Button
        loading={isSubmitting}
        disabled={password.length === 0 || confirm.length === 0 || isSubmitting}
        onClick={handleSubmit}
      >
        {strings.password.saveButton}
      </Button>
    </>
  );
}
